import { useQueryClient } from '@tanstack/react-query';
import { useRef, type ComponentProps } from 'react';
import { startTransition, useDeferredValue, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { ReactSelectInput } from '@hivebrite/design-system/Inputs';
import { type OptionType } from '@hivebrite/design-system/Inputs';

import { useCurrentNetwork } from 'hb-react/shared/contexts';
import { captureException } from 'hb-react/shared/utils/errorReporting';
import { type Topic } from 'hb-react/types';

import { useQueryGroups } from './hooks/use-query-groups';

const addValidTopic = (
  topic: OptionType | Topic | null | undefined,
  uniqueTopicsMap: Map<number, OptionType | Topic>,
) => {
  if (!topic) {
    return;
  }

  const { id, name } = topic;

  if (id && name) {
    uniqueTopicsMap.set(id, topic);
  }
};

const ReactSelectTopics = ({
  id,
  name,
  ...props
}: ComponentProps<typeof ReactSelectInput>) => {
  const queryClient = useQueryClient();
  const {
    currentNetwork: { id: networkId },
  } = useCurrentNetwork();
  const [inputValue, setInputValue] = useState('');
  const defferedInputValue = useDeferredValue(inputValue);

  const { data: topics = [], isLoading } = useQueryGroups({
    networkId,
    params: { fulltext: defferedInputValue, limit: 25 },
  });

  const onInputChange = useDebouncedCallback((value, { action }) => {
    if (action === 'input-change') {
      startTransition(() => {
        setInputValue(value);
      });
    }
  }, 500);

  const initialValue = useRef(props.value);

  const uniqueTopicsMap = useMemo(() => {
    const map = new Map<number, OptionType>();

    try {
      topics?.forEach((topic) => addValidTopic(topic, map));

      const cachedQueries = queryClient.getQueriesData({
        queryKey: ['groups', networkId],
      });

      cachedQueries.forEach(([_queryKey, cachedData]) => {
        if (Array.isArray(cachedData)) {
          cachedData.forEach((topic) => addValidTopic(topic, map));
        }
      });

      const initialValues = Array.isArray(initialValue.current)
        ? initialValue.current
        : [initialValue.current];

      initialValues.forEach((value) => addValidTopic(value, map));

      return map;
    } catch (error) {
      captureException(error);

      return new Map();
    }
  }, [topics, queryClient, networkId]);

  const options = useMemo(
    (): Array<OptionType> =>
      Array.from(uniqueTopicsMap.values()).map(
        ({ id: value, name: label, accessibleByCurrentAdmin }) => ({
          value,
          label,
          isFixed:
            accessibleByCurrentAdmin === undefined
              ? false
              : !accessibleByCurrentAdmin,
        }),
      ),
    [uniqueTopicsMap],
  );

  return (
    <ReactSelectInput
      placeholder={__('Search and select groups')}
      isMulti
      {...props}
      id={id}
      name={name}
      options={options}
      isLoading={isLoading}
      // @ts-expect-error react-select is not typed
      onInputChange={onInputChange}
    />
  );
};

export default ReactSelectTopics;
